/**
 * 管理模式路由守卫组件
 * 检查用户是否在管理模式下
 * - 有权限但未进入管理模式：自动进入管理模式
 * - 无权限：重定向到业务首页
 */

import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAdminMode } from '@/modules/foundation/contexts/AdminModeContext';

export interface AdminModeRouteProps {
  children: React.ReactNode;
  /** 没有管理模式权限时的重定向路径 */
  redirectTo?: string;
}

const AdminModeRoute: React.FC<AdminModeRouteProps> = ({
  children,
  redirectTo = '/tenant/dashboard',
}) => {
  const location = useLocation();
  const { isInAdminMode, hasPermission, isLoading, enterAdminMode, isStatusLoaded } = useAdminMode();
  const [isAutoEntering, setIsAutoEntering] = useState(false);
  const [autoEnterFailed, setAutoEnterFailed] = useState(false);

  // 如果有权限但不在管理模式，自动进入管理模式（仅在状态加载完成后）
  useEffect(() => {
    const autoEnter = async () => {
      // 等待状态加载完成后再尝试自动进入
      if (isStatusLoaded && hasPermission && !isInAdminMode && !isLoading && !isAutoEntering && !autoEnterFailed) {
        setIsAutoEntering(true);
        console.log('AdminModeRoute: 自动进入管理模式');
        const success = await enterAdminMode();
        setIsAutoEntering(false);
        if (!success) {
          setAutoEnterFailed(true);
        }
      }
    };
    autoEnter();
  }, [isStatusLoaded, hasPermission, isInAdminMode, isLoading, isAutoEntering, autoEnterFailed, enterAdminMode]);

  // 正在加载、等待状态加载完成、或正在自动进入管理模式
  if (isLoading || isAutoEntering || !isStatusLoaded) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: 300,
      }}>
        <Spin size="large">
          <div style={{ minHeight: 100, padding: 50 }}>
            正在进入管理模式...
          </div>
        </Spin>
      </div>
    );
  }

  // 状态已加载，如果没有权限，重定向到业务首页
  if (!hasPermission) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location }}
        replace
      />
    );
  }

  // 如果自动进入失败，重定向到业务首页
  if (autoEnterFailed && !isInAdminMode) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location, adminModeError: true }}
        replace
      />
    );
  }

  // 如果还不在管理模式（等待自动进入完成）
  if (!isInAdminMode) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: 300,
      }}>
        <Spin size="large">
          <div style={{ minHeight: 100, padding: 50 }}>
            正在进入管理模式...
          </div>
        </Spin>
      </div>
    );
  }

  // 管理模式有效，渲染子组件
  return <>{children}</>;
};

export default AdminModeRoute;
